import type { ApiErrorCode } from '@lumos/contracts';
import { API_PREFIX, APP_VERSION_HEADER_NAME, CSRF_HEADER_NAME } from './wire.js';

/**
 * Единственная точка сетевого доступа клиента.
 *
 * Клиент ходит ТОЛЬКО на свой origin и только под префиксом `/api/v1`:
 * внешние URL (TMDB, трекеры, image CDN) наружу не выходят — их знает лишь
 * backend (ARCHITECTURE.md §7.1). Поэтому здесь принимается относительный
 * путь, а не полный URL.
 */

/** Клиентский код ошибки для случаев, когда ответ API получить не удалось. */
export type ClientErrorCode = 'NETWORK_ERROR' | 'TIMEOUT' | 'MALFORMED_RESPONSE';

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Double-submit CSRF-токен текущей сессии. Приходит в теле `/auth/state` и
 * `/auth/login`, но НЕ является единственным источником истины — см. ниже.
 */
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/** Имя JS-доступной cookie с CSRF-токеном (сервер: `CSRF_COOKIE` в session.ts). */
const CSRF_COOKIE = 'oc_csrf';

/**
 * Актуальный CSRF-токен: сперва cookie, и только потом запомненный.
 *
 * Запомненного мало, потому что сессия обновляется САМА. Пока устройство
 * одобрено, истёкший access-токен молча меняется на новый прямо в обработке
 * очередного запроса — вместе с ним меняется и csrf-токен. Клиент об этом не
 * знает, продолжает слать старый и получает «Не пройдена проверка CSRF-токена»
 * на ровном месте: чаще всего на первом же действии после долгой паузы во
 * вкладке. Cookie же сервер обновляет в том же ответе, поэтому читать её на
 * каждом запросе — единственный способ не разъехаться.
 */
function currentCsrfToken(): string | null {
  const match = new RegExp(`(?:^|;\\s*)${CSRF_COOKIE}=([^;]*)`).exec(document.cookie);
  const fromCookie = match?.[1];
  if (fromCookie !== undefined && fromCookie !== '') return decodeURIComponent(fromCookie);
  return csrfToken;
}

export class ApiError extends Error {
  readonly code: ApiErrorCode | ClientErrorCode;
  readonly status: number;
  readonly requestId: string | null;
  readonly details: Record<string, unknown> | null;

  constructor(
    code: ApiErrorCode | ClientErrorCode,
    message: string,
    options: {
      status?: number;
      requestId?: string | null;
      details?: Record<string, unknown> | null;
      cause?: unknown;
    } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'ApiError';
    this.code = code;
    this.status = options.status ?? 0;
    this.requestId = options.requestId ?? null;
    this.details = options.details ?? null;
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Не заворачивать чтение в обход: запрос уже завёрнут либо сам сверяет cookie. */
  direct?: boolean;
  /**
   * Тип тела, если это не JSON: например картинка профиля, которая едет файлом
   * как есть. Тогда тело уходит без `JSON.stringify` — иначе на сервер приехала
   * бы строка «{}» вместо байтов.
   */
  contentType?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Разбор тела ошибки API. Формат зафиксирован в contracts/errors.ts. */
function parseApiError(payload: unknown, status: number): ApiError {
  if (isRecord(payload) && isRecord(payload.error)) {
    const error = payload.error;
    const code = typeof error.code === 'string' ? (error.code as ApiErrorCode) : 'INTERNAL_ERROR';
    const message =
      typeof error.message === 'string' && error.message.length > 0
        ? error.message
        : 'Запрос завершился ошибкой';
    return new ApiError(code, message, {
      status,
      requestId: typeof error.requestId === 'string' ? error.requestId : null,
      details: isRecord(error.details) ? error.details : null,
    });
  }
  return new ApiError('INTERNAL_ERROR', `Ошибка запроса (HTTP ${String(status)})`, { status });
}

/**
 * Таймаут через собственный AbortController: `AbortSignal.timeout` доступен
 * не во всех TV-браузерах, на которые нацелен build target.
 */
function withTimeout(
  external: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; dispose: () => void; timedOut: () => boolean } {
  const controller = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const onExternalAbort = () => {
    controller.abort();
  };
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener('abort', onExternalAbort);
  }

  return {
    signal: controller.signal,
    dispose: () => {
      clearTimeout(timer);
      external?.removeEventListener('abort', onExternalAbort);
    },
    timedOut: () => timedOut,
  };
}

/**
 * @param path путь относительно `/api/v1`, например `/catalog/home`.
 */
const PROFILE_HEADER_NAME = 'x-profile-id';
/**
 * Версия веб-клиента. Подставляется сборщиком из `package.json`, поэтому в
 * мониторе видно ту сборку, которая реально открыта у зрителя, — а не ту,
 * которую в этот момент раздаёт сервер: вкладка живёт неделями и переживает
 * несколько обновлений.
 */
const APP_VERSION = __LUMOS_WEB_VERSION__;
const PROFILE_STORAGE_KEY = 'lumos.profile';

/** Выбранный профиль. Живёт в localStorage: он про этот браузер, а не аккаунт. */
export function currentProfileId(): string | null {
  try {
    return window.localStorage.getItem(PROFILE_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setCurrentProfileId(profileId: string | null): void {
  try {
    if (profileId === null) window.localStorage.removeItem(PROFILE_STORAGE_KEY);
    else window.localStorage.setItem(PROFILE_STORAGE_KEY, profileId);
  } catch {
    // Приватный режим без хранилища: профиль просто не запомнится.
  }
}

/**
 * Спрашивали ли «кто смотрит?» в ЭТОЙ вкладке.
 *
 * `sessionStorage`, а не `localStorage`: он живёт ровно столько, сколько
 * вкладка, и это в точности то, что нужно спросить заново. Закрыл вкладку —
 * ушёл из приложения, и в следующий раз к тому же компьютеру мог сесть другой
 * человек; перезагрузил страницу — никуда не уходил, и переспрашивать значило
 * бы делать F5 платным.
 *
 * Выбранный профиль при этом остаётся в `localStorage`: он помнит, КТО смотрел
 * прошлый раз, и именно с ним сверяется новый выбор.
 */
const PROFILE_ASKED_KEY = 'lumos.profile.asked';

export function profileAskedInThisTab(): boolean {
  try {
    return window.sessionStorage.getItem(PROFILE_ASKED_KEY) === '1';
  } catch {
    // Без хранилища спрашивать на каждой странице — хуже, чем не спрашивать
    // вовсе: экран выбора встал бы между зрителем и любым переходом.
    return true;
  }
}

export function rememberProfileAsked(asked: boolean): void {
  try {
    if (asked) window.sessionStorage.setItem(PROFILE_ASKED_KEY, '1');
    else window.sessionStorage.removeItem(PROFILE_ASKED_KEY);
  } catch {
    // См. выше: молча живём дальше.
  }
}

/**
 * Чтение в обход: браузер не передаёт cookie в запросах чтения, и они уходят
 * методом POST на `/lite/read` — сервер исполняет у себя обычный GET с теми же
 * cookie. Включается само (см. `getAuthState`), ключ общий с лёгкой версией.
 */
const VIA_POST_KEY = 'lumos.lite.viaPost';
let viaPost = (() => {
  try {
    return window.localStorage.getItem(VIA_POST_KEY) === '1';
  } catch {
    return false;
  }
})();
export function readsViaPost(): boolean {
  return viaPost;
}
export function useReadsViaPost(): void {
  viaPost = true;
  try {
    window.localStorage.setItem(VIA_POST_KEY, '1');
  } catch {
    // Не запомнится — и только.
  }
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  if (!path.startsWith('/')) {
    throw new ApiError('MALFORMED_RESPONSE', `Некорректный путь API: ${path}`);
  }
  if (viaPost && (options.method ?? 'GET') === 'GET' && options.direct !== true) {
    return apiRequest<T>('/lite/read', { ...options, method: 'POST', body: { path }, direct: true });
  }

  const timeout = withTimeout(options.signal, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = options.contentType ?? 'application/json';
  // CSRF нужен только изменяющим запросам: серверная проверка (session.ts)
  // применяется исключительно к cookie-аутентификации, но заголовок безвреден
  // и для остальных случаев, поэтому проставляем его для любого не-GET.
  const csrf = method === 'GET' ? null : currentCsrfToken();
  if (csrf) headers[CSRF_HEADER_NAME] = csrf;
  // Выбранный профиль едет в каждом запросе: избранное, история и прогресс у
  // каждого свои. Сервер принимает заголовок, только если профиль привязан к
  // этому устройству, — подставить чужой нельзя.
  const profile = currentProfileId();
  if (profile) headers[PROFILE_HEADER_NAME] = profile;
  headers[APP_VERSION_HEADER_NAME] = APP_VERSION;

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method,
      headers,
      // Cookie сессии и persistent device-credential идут с каждым запросом.
      credentials: 'same-origin',
      body:
        options.body === undefined
          ? undefined
          : options.contentType === undefined
            ? JSON.stringify(options.body)
            : (options.body as BodyInit),
      signal: timeout.signal,
    });
  } catch (error) {
    if (timeout.timedOut()) {
      throw new ApiError('TIMEOUT', 'Сервер не ответил вовремя', { cause: error });
    }
    if (options.signal?.aborted) {
      throw new ApiError('NETWORK_ERROR', 'Запрос отменён', { cause: error });
    }
    throw new ApiError('NETWORK_ERROR', 'Нет связи с сервером', { cause: error });
  } finally {
    timeout.dispose();
  }

  const raw = await response.text();
  let payload: unknown = null;
  if (raw.length > 0) {
    try {
      payload = JSON.parse(raw);
    } catch (error) {
      if (!response.ok) throw parseApiError(null, response.status);
      throw new ApiError('MALFORMED_RESPONSE', 'Сервер вернул некорректный ответ', {
        status: response.status,
        cause: error,
      });
    }
  }

  if (!response.ok) throw parseApiError(payload, response.status);
  return payload as T;
}

/** Сообщение, которое можно показать пользователю без утечки внутренних деталей. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message.length > 0) return error.message;
  return 'Непредвиденная ошибка';
}
